<!--
    AI:
    Status: 'generated'
    Type: Component
    SubType: Configuration / Read-only Overview
    Reason: Read-only presentation of the deadline and level configuration of a
            bd.DunningType. Hand-built panel (capability matrix row H3, signed)
            instead of a generic table widget, because the level schedule interleaves
            level x offsets x example data x actions.
            The start conditions are grouped per billing area in a p-accordion
            (groups + expand state come from the component, never from a template
            method), because the flat list is unusable on a productive tenant.
            No edit controls, no dialogs, no save paths.

    @ticket bd-dunningtype-overview
-->
@if (initialized) {
    <div class="bd-dunning-type-overview flex flex-column gap-3 p-3">

        <!-- ============================================================ -->
        <!-- Scope and character of this view                             -->
        <!-- (business rules 2 and 6: illustration, not an actual date)    -->
        <!-- ============================================================ -->
        <p-message severity="info"
                   text="Diese Ansicht ist rein lesend. Die Beispieldaten sind eine Illustration für das gewählte Musterdatum und kein Termin einer konkreten Mahnung. Änderungen an der Konfiguration nehmen Sie über die Navigationspunkte der Mahnart (z. B. „Stufen“, „Startbedingungen“) oder in der Knotenansicht vor."
                   styleClass="w-full">
        </p-message>

        <!-- ============================================================ -->
        <!-- 1. Header block (requirement 7.1)                            -->
        <!-- ============================================================ -->
        <div class="surface-card border-round-lg border-1 border-300 p-3">
            <div class="flex align-items-center gap-2 mb-3">
                <i class="fa-regular fa-file-invoice-dollar text-primary"></i>
                <span class="text-lg font-semibold">Mahnungstyp</span>
                @if (headerStatusLabel) {
                    <p-tag [value]="headerStatusLabel"
                           [severity]="headerStatusLabel === 'Aktiv' ? 'success' : 'warn'">
                    </p-tag>
                }
            </div>

            <div class="grid">
                <div class="col-12 md:col-6 xl:col-4 flex flex-column gap-1 mb-2">
                    <span class="text-xs font-bold text-gray-600 uppercase">Name</span>
                    <span class="font-medium">{{ headerName || '—' }}</span>
                </div>
                <div class="col-12 md:col-6 xl:col-4 flex flex-column gap-1 mb-2">
                    <span class="text-xs font-bold text-gray-600 uppercase">Maximale Mahnpause</span>
                    <span class="font-medium">{{ headerMaxPauseLabel }}</span>
                </div>
                <div class="col-12 md:col-6 xl:col-4 flex flex-column gap-1 mb-2">
                    <span class="text-xs font-bold text-gray-600 uppercase">Mahnungs-PDF-Vorlage</span>
                    <span class="font-medium">{{ headerPrintTemplateLabel || 'Keine Vorlage hinterlegt' }}</span>
                </div>
                <div class="col-12 md:col-6 xl:col-4 flex flex-column gap-1 mb-2">
                    <span class="text-xs font-bold text-gray-600 uppercase">Verteilungs-Reihenfolge</span>
                    <span class="font-medium">{{ headerAllocationOrderLabel || 'Nicht konfiguriert' }}</span>
                </div>
                @if (headerDescription) {
                    <div class="col-12 flex flex-column gap-1">
                        <span class="text-xs font-bold text-gray-600 uppercase">Beschreibung</span>
                        <span class="text-sm">{{ headerDescription }}</span>
                    </div>
                }
            </div>
        </div>

        <!-- ============================================================ -->
        <!-- Reference date (what-if input, requirement 11.3)             -->
        <!-- Changes ONLY the example calculation; nothing is persisted.   -->
        <!-- ============================================================ -->
        <div class="surface-card border-round-lg border-1 border-300 p-3">
            <div class="flex align-items-center gap-2 mb-3">
                <i class="fa-regular fa-calendar-day text-primary"></i>
                <span class="text-lg font-semibold">Musterdatum der Beispielrechnung</span>
            </div>

            <div class="flex align-items-center gap-3 flex-wrap">
                <label for="bdDunningTypeOverviewReferenceDate" class="font-medium">
                    Lauf-/Versanddatum:
                </label>
                <p-datePicker [ngModel]="referenceDate"
                              inputId="bdDunningTypeOverviewReferenceDate"
                              dateFormat="dd.mm.yy"
                              placeholder="TT.MM.JJJJ"
                              [appendTo]="'body'"
                              pTooltip="Das Datum, mit dem der Mahnlauf rechnen würde. Alle Beispieldaten unten beziehen sich darauf."
                              (ngModelChange)="onReferenceDateSelected($event)">
                </p-datePicker>

                <p-button label="Heute"
                          icon="fa-regular fa-rotate-left"
                          severity="secondary"
                          [text]="true"
                          [disabled]="derivationLoading"
                          (onClick)="resetReferenceDate()">
                </p-button>

                @if (derivationLoading) {
                    <span class="text-sm text-gray-600">Beispielrechnung wird geladen …</span>
                }
            </div>
        </div>

        <!-- ============================================================ -->
        <!-- 2. Entry points / paths A, B, C (requirement 7.2)            -->
        <!-- ============================================================ -->
        <div class="surface-card border-round-lg border-1 border-300 p-3">
            <div class="flex align-items-center gap-2 mb-3 flex-wrap">
                <i class="fa-regular fa-signs-post text-primary"></i>
                <span class="text-lg font-semibold">Einstieg in das Mahnverfahren (Pfade)</span>
                @if (startSummaryLabel) {
                    <span class="text-sm text-gray-600">{{ startSummaryLabel }}</span>
                }

                <!-- Expand/collapse all only pays off from the second group onwards. -->
                @if (startGroups.length > 1) {
                    <div class="flex align-items-center gap-1 ml-auto">
                        <p-button label="Alle aufklappen"
                                  icon="fa-solid fa-chevron-down"
                                  severity="secondary"
                                  [text]="true"
                                  (onClick)="expandAllStartGroups()">
                        </p-button>
                        <p-button label="Alle zuklappen"
                                  icon="fa-solid fa-chevron-up"
                                  severity="secondary"
                                  [text]="true"
                                  (onClick)="collapseAllStartGroups()">
                        </p-button>
                    </div>
                }
            </div>

            @if (structureLoading) {
                <div class="flex flex-column gap-2">
                    <p-skeleton height="3rem"></p-skeleton>
                    <p-skeleton height="3rem"></p-skeleton>
                </div>
            } @else if (structureError) {
                <p-message severity="error"
                           text="Die Startbedingungen konnten nicht geladen werden. Bitte laden Sie die Seite erneut."
                           styleClass="w-full">
                </p-message>
            } @else if (!starts.length) {
                <p-message severity="warn"
                           text="Für diesen Mahnungstyp ist keine Startbedingung konfiguriert — das Mahnverfahren startet damit über keinen der Pfade. Legen Sie eine Startbedingung unter „Startbedingungen“ an."
                           styleClass="w-full">
                </p-message>
            } @else {
                <!-- One panel per billing area. The groups come pre-built from the
                     component (startGroups), the expanded keys are bound two-way. -->
                <p-accordion [multiple]="true" [(value)]="expandedStartGroupKeys">
                    @for (group of startGroups; track group.key) {
                        <p-accordion-panel [value]="group.key">
                            <p-accordion-header>
                                <span class="flex align-items-center gap-2 flex-wrap">
                                    <i class="fa-regular fa-building-columns text-primary"></i>
                                    <span class="font-semibold">{{ group.label }}</span>
                                    <p-tag [value]="pathCountLabel(group.starts.length)"
                                           severity="secondary">
                                    </p-tag>
                                    @if (group.inactiveCount > 0) {
                                        <p-tag [value]="group.inactiveCount + ' inaktiv'" severity="warn"></p-tag>
                                    }
                                </span>
                            </p-accordion-header>

                            <p-accordion-content>
                                <div class="flex flex-column gap-2">
                                    @for (start of group.starts; track start.id) {
                                        <div class="border-round-md border-1 border-300 p-3">
                                            <div class="flex align-items-center gap-2 mb-2 flex-wrap">
                                                <span class="font-semibold">{{ start.name || 'Ohne Bezeichnung' }}</span>
                                                @if (isInactive(start.entityStatus)) {
                                                    <p-tag [value]="entityStatusLabel(start.entityStatus)"
                                                           severity="warn">
                                                    </p-tag>
                                                }
                                                @if (start.startLevelDtoName) {
                                                    <p-tag [value]="'Startstufe: ' + start.startLevelDtoName"
                                                           severity="info">
                                                    </p-tag>
                                                }
                                            </div>

                                            <!-- The billing area is the panel header and is therefore
                                                 no longer repeated per row. -->
                                            <div class="grid">
                                                <div class="col-12 md:col-6 xl:col-4 flex flex-column gap-1 mb-2">
                                                    <span class="text-xs font-bold text-gray-600 uppercase">Zahlart</span>
                                                    <span class="text-sm">{{ paymentMethodLabel(start.paymentMethod) }}</span>
                                                </div>
                                                <div class="col-12 md:col-6 xl:col-4 flex flex-column gap-1 mb-2">
                                                    <span class="text-xs font-bold text-gray-600 uppercase">Mindestbetrag</span>
                                                    <span class="text-sm">
                                                        {{ start.minAmount != null ? (start.minAmount | number:'1.2-2') + ' €' : 'Kein Mindestbetrag' }}
                                                    </span>
                                                </div>
                                                <div class="col-12 md:col-6 xl:col-4 flex flex-column gap-1 mb-2">
                                                    <span class="text-xs font-bold text-gray-600 uppercase">
                                                        Schwellwert negative Periode
                                                    </span>
                                                    <span class="text-sm">
                                                        {{ durationLabel(start.thresholdNegativePeriod) || 'Nicht gesetzt' }}
                                                    </span>
                                                </div>
                                            </div>

                                            <!-- Requirement 7.11: check logics are NAMED only, never evaluated. -->
                                            @if (start.checkLogicApply && start.checkLogicDtoName) {
                                                <div class="flex align-items-center gap-2 text-sm text-blue-700">
                                                    <i class="fa-regular fa-code-branch"></i>
                                                    <span>Hier greift zusätzlich eine Prüflogik: <strong>{{ start.checkLogicDtoName }}</strong></span>
                                                </div>
                                            }
                                        </div>
                                    }
                                </div>
                            </p-accordion-content>
                        </p-accordion-panel>
                    }
                </p-accordion>
            }
        </div>

        <!-- ============================================================ -->
        <!-- 3. Dunning level schedule (core block, requirement 7.3)      -->
        <!-- including actions (7.6) and special handlings (7.9)           -->
        <!-- ============================================================ -->
        <div class="surface-card border-round-lg border-1 border-300 p-3">
            <div class="flex align-items-center gap-2 mb-3 flex-wrap">
                <i class="fa-regular fa-list-check text-primary"></i>
                <span class="text-lg font-semibold">Mahnstufen-Fahrplan</span>
                <span class="text-sm text-gray-600">
                    Beispielrechnung für den {{ referenceDate | date:'dd.MM.yyyy' }}
                </span>
            </div>

            @if (structureLoading) {
                <div class="flex flex-column gap-2">
                    <p-skeleton height="8rem"></p-skeleton>
                    <p-skeleton height="8rem"></p-skeleton>
                </div>
            } @else if (structureError) {
                <p-message severity="error"
                           text="Die Mahnstufen konnten nicht geladen werden. Bitte laden Sie die Seite erneut."
                           styleClass="w-full">
                </p-message>
            } @else if (!levels.length) {
                <p-message severity="warn"
                           text="Für diesen Mahnungstyp ist keine Mahnstufe konfiguriert. Legen Sie die Stufen (M1…M4) unter „Stufen“ an."
                           styleClass="w-full">
                </p-message>
            } @else {
                @if (derivationError) {
                    <p-message severity="error"
                               text="Die Fristen-Ableitung konnte nicht geladen werden. Die Stufen werden ohne aufgelöste Offsets und ohne Beispieldaten angezeigt."
                               styleClass="w-full mb-3">
                    </p-message>
                }

                <div class="flex flex-column gap-3">
                    @for (level of levels; track level.id) {
                        @let derivation = derivationFor(level);

                        <div class="border-round-md border-1 border-300 p-3">

                            <!-- Level header -->
                            <div class="flex align-items-center gap-2 mb-3 flex-wrap">
                                <p-tag [value]="level.shortName || '—'" severity="secondary"></p-tag>
                                <span class="text-base font-semibold">{{ level.name || 'Ohne Bezeichnung' }}</span>
                                @if (isInactive(level.entityStatus)) {
                                    <p-tag [value]="entityStatusLabel(level.entityStatus)" severity="warn"></p-tag>
                                }
                            </div>

                            @if (level.description) {
                                <p class="text-sm text-gray-600 mt-0 mb-3">{{ level.description }}</p>
                            }

                            <!-- The four Model-C offsets with their provenance badge.
                                 An empty count field means the offset does NOT APPLY
                                 (counts as 0 days, tag "nicht konfiguriert") — there is
                                 no system default any more (business rule 1). -->
                            @if (derivationLoading) {
                                <p-skeleton height="4rem" styleClass="mb-3"></p-skeleton>
                            } @else if (derivation) {
                                <div class="grid mb-3">
                                    <div class="col-12 md:col-6 xl:col-3 flex flex-column gap-1 mb-2">
                                        <span class="text-xs font-bold text-gray-600 uppercase">Postlauf</span>
                                        <div class="flex align-items-center gap-2 flex-wrap">
                                            <span class="font-medium">
                                                {{ offsetValueLabel(derivation.postalTransit) }}
                                            </span>
                                            <p-tag [value]="offsetSourceLabel(derivation.postalTransit)"
                                                   [severity]="offsetSourceSeverity(derivation.postalTransit)">
                                            </p-tag>
                                        </div>
                                    </div>
                                    <div class="col-12 md:col-6 xl:col-3 flex flex-column gap-1 mb-2">
                                        <span class="text-xs font-bold text-gray-600 uppercase">Zahlungsfrist</span>
                                        <div class="flex align-items-center gap-2 flex-wrap">
                                            <span class="font-medium">
                                                {{ offsetValueLabel(derivation.paymentTerm) }}
                                            </span>
                                            <p-tag [value]="offsetSourceLabel(derivation.paymentTerm)"
                                                   [severity]="offsetSourceSeverity(derivation.paymentTerm)">
                                            </p-tag>
                                        </div>
                                    </div>
                                    <div class="col-12 md:col-6 xl:col-3 flex flex-column gap-1 mb-2">
                                        <span class="text-xs font-bold text-gray-600 uppercase">Interner Puffer</span>
                                        <div class="flex align-items-center gap-2 flex-wrap">
                                            <span class="font-medium">
                                                {{ offsetValueLabel(derivation.buffer) }}
                                            </span>
                                            <p-tag [value]="offsetSourceLabel(derivation.buffer)"
                                                   [severity]="offsetSourceSeverity(derivation.buffer)">
                                            </p-tag>
                                        </div>
                                    </div>
                                    <div class="col-12 md:col-6 xl:col-3 flex flex-column gap-1 mb-2">
                                        <span class="text-xs font-bold text-gray-600 uppercase">
                                            Karenz Mahnstart (Pfad A)
                                        </span>
                                        <div class="flex align-items-center gap-2 flex-wrap">
                                            <span class="font-medium">
                                                {{ offsetValueLabel(derivation.gracePeriod) }}
                                            </span>
                                            <p-tag [value]="offsetSourceLabel(derivation.gracePeriod)"
                                                   [severity]="offsetSourceSeverity(derivation.gracePeriod)">
                                            </p-tag>
                                        </div>
                                    </div>
                                </div>

                                <!-- The formula spelled out in words (requirement 7.3) -->
                                <div class="bg-blue-50 border-round-md p-3 mb-3">
                                    <div class="flex align-items-start gap-2 mb-2">
                                        <i class="fa-regular fa-calculator text-blue-600 mt-1"></i>
                                        <span class="text-sm">{{ formulaText(derivation) }}</span>
                                    </div>
                                    <div class="flex align-items-start gap-2">
                                        <i class="fa-regular fa-flag text-blue-600 mt-1"></i>
                                        <span class="text-sm">{{ mahnstartFormulaText(derivation) }}</span>
                                    </div>
                                </div>

                                <!-- Example date chain (requirement 7.3) -->
                                <div class="grid mb-2">
                                    <div class="col-12 md:col-6 xl:col-3 flex flex-column gap-1 mb-2">
                                        <span class="text-xs font-bold text-gray-600 uppercase">
                                            Mahnstart (Pfad A)
                                        </span>
                                        <span class="font-medium">
                                            {{ derivation.mahnstartDate | date:'dd.MM.yyyy' }}
                                        </span>
                                    </div>
                                    <div class="col-12 md:col-6 xl:col-3 flex flex-column gap-1 mb-2">
                                        <span class="text-xs font-bold text-gray-600 uppercase">
                                            Kommunizierter Stichtag
                                        </span>
                                        <span class="font-medium">
                                            {{ derivation.communicatedDeadline | date:'dd.MM.yyyy' }}
                                        </span>
                                        <span class="text-xs text-gray-600">steht im Kundenbrief</span>
                                    </div>
                                    <div class="col-12 md:col-6 xl:col-3 flex flex-column gap-1 mb-2">
                                        <span class="text-xs font-bold text-gray-600 uppercase">
                                            Interner Stichtag
                                        </span>
                                        <span class="font-medium">
                                            {{ derivation.internalDeadline | date:'dd.MM.yyyy' }}
                                        </span>
                                        <span class="text-xs text-gray-600">löst die Eskalation aus</span>
                                    </div>
                                    <div class="col-12 md:col-6 xl:col-3 flex flex-column gap-1 mb-2">
                                        <span class="text-xs font-bold text-gray-600 uppercase">
                                            Gesamtdauer
                                        </span>
                                        <span class="font-medium">
                                            {{ derivation.totalCalendarDays != null
                                                ? derivation.totalCalendarDays + ' Kalendertage'
                                                : '—' }}
                                        </span>
                                        <span class="text-xs text-gray-600">
                                            Musterdatum bis interner Stichtag
                                        </span>
                                    </div>
                                </div>

                                <!-- Roll of the communicated deadline: shown only when the level's
                                     configured roll mode actually moved the date. -->
                                @if (derivation.rolledByParagraph193) {
                                    <div class="flex align-items-center gap-2 text-sm text-yellow-700 mb-2">
                                        <i class="fa-solid fa-triangle-exclamation"></i>
                                        <span>
                                            Der kommunizierte Stichtag wurde verschoben: die Frist fiel auf einen
                                            Samstag, Sonntag oder Feiertag und rollte gemäß der für diese Stufe
                                            konfigurierten Verschiebung auf den nächsten Bankarbeitstag.
                                        </span>
                                    </div>
                                }
                            } @else {
                                <p-message severity="warn"
                                           text="Für diese Stufe liegt keine Fristen-Ableitung vor. Die wirksamen Offsets und die Beispieldaten können deshalb nicht angezeigt werden."
                                           styleClass="w-full mb-3">
                                </p-message>
                            }

                            <!-- Actions of this level (requirement 7.6) -->
                            <p-divider></p-divider>
                            <div class="flex align-items-center gap-2 mb-2">
                                <i class="fa-regular fa-bolt text-gray-600"></i>
                                <span class="font-medium">Aktionen dieser Stufe</span>
                            </div>

                            @if (!actionsFor(level).length) {
                                <span class="text-sm text-gray-600">
                                    Für diese Stufe ist keine Aktion konfiguriert — beim Erreichen der Stufe
                                    passiert nichts automatisch.
                                </span>
                            } @else {
                                <div class="flex flex-column gap-2">
                                    @for (action of actionsFor(level); track action.id) {
                                        <div class="border-round-md border-1 border-300 p-2">
                                            <div class="flex align-items-center gap-2 flex-wrap mb-1">
                                                <span class="font-medium text-sm">
                                                    {{ action.name || 'Ohne Bezeichnung' }}
                                                </span>
                                                @if (actionTypeLabel(action.actionType); as actionTypeText) {
                                                    <p-tag [value]="actionTypeText" severity="info"></p-tag>
                                                }
                                                @if (actionExecutionTypeLabel(action.actionExecutionType); as executionText) {
                                                    <p-tag [value]="executionText" severity="secondary"></p-tag>
                                                }
                                                @if (actionTimingLabel(action.actionTiming); as timingText) {
                                                    <p-tag [value]="timingText" severity="secondary"></p-tag>
                                                }
                                                @if (isInactive(action.entityStatus)) {
                                                    <p-tag [value]="entityStatusLabel(action.entityStatus)"
                                                           severity="warn">
                                                    </p-tag>
                                                }
                                            </div>

                                            <div class="flex flex-wrap gap-3 text-sm text-gray-600">
                                                @if (action.notificationTypeDtoName) {
                                                    <span>
                                                        Benachrichtigung:
                                                        <strong>{{ action.notificationTypeDtoName }}</strong>
                                                    </span>
                                                }
                                                @if (action.chargeAmount != null) {
                                                    <span>
                                                        Gebühr:
                                                        <strong>{{ action.chargeAmount | number:'1.2-2' }} €</strong>
                                                    </span>
                                                }
                                                @if (action.chargeTransactionTypeDtoName) {
                                                    <span>
                                                        Buchungsart:
                                                        <strong>{{ action.chargeTransactionTypeDtoName }}</strong>
                                                    </span>
                                                }
                                                @if (chargeValutaDateTimingLabel(action.chargeValutaDateTiming); as valutaText) {
                                                    <span>Valutadatum: <strong>{{ valutaText }}</strong></span>
                                                }
                                                @if (action.ticketTypeDtoName) {
                                                    <span>Tickettyp: <strong>{{ action.ticketTypeDtoName }}</strong></span>
                                                }
                                                @if (action.fallbackForActionDtoName) {
                                                    <span>
                                                        Fallback für:
                                                        <strong>{{ action.fallbackForActionDtoName }}</strong>
                                                    </span>
                                                }
                                                @if (action.checkLogicApply && action.checkLogicDtoName) {
                                                    <span class="text-blue-700">
                                                        Prüflogik: <strong>{{ action.checkLogicDtoName }}</strong>
                                                    </span>
                                                }
                                            </div>
                                        </div>
                                    }
                                </div>
                            }

                            <!-- Special handlings of this level (requirement 7.9) -->
                            @if (specialHandlingsFor(level).length) {
                                <p-divider></p-divider>
                                <div class="flex align-items-center gap-2 mb-2">
                                    <i class="fa-regular fa-hand text-gray-600"></i>
                                    <span class="font-medium">Sonderbehandlungen dieser Stufe</span>
                                </div>
                                <div class="flex flex-column gap-2">
                                    @for (handling of specialHandlingsFor(level); track handling.id) {
                                        <div class="flex align-items-center gap-2 flex-wrap text-sm">
                                            <span class="font-medium">
                                                {{ handling.name || 'Ohne Bezeichnung' }}
                                            </span>
                                            @if (specialHandlingTypeLabel(handling.handlingType); as handlingText) {
                                                <p-tag [value]="handlingText" severity="info"></p-tag>
                                            }
                                            @if (isInactive(handling.entityStatus)) {
                                                <p-tag [value]="entityStatusLabel(handling.entityStatus)"
                                                       severity="warn">
                                                </p-tag>
                                            }
                                            @if (handling.selectionLogicApply && handling.selectionLogicDtoName) {
                                                <span class="text-blue-700">
                                                    Auswahllogik: <strong>{{ handling.selectionLogicDtoName }}</strong>
                                                </span>
                                            }
                                        </div>
                                    }
                                </div>
                            }
                        </div>
                    }
                </div>
            }
        </div>

        <!-- ============================================================ -->
        <!-- 4. Calendar explanation (requirement 7.4)                    -->
        <!-- NO country claim, no tenant claim (FD-7, signed)              -->
        <!-- ============================================================ -->
        <div class="surface-card border-round-lg border-1 border-300 p-3">
            <div class="flex align-items-center gap-2 mb-3">
                <i class="fa-regular fa-calendar-days text-primary"></i>
                <span class="text-lg font-semibold">Bankarbeitstage und Feiertage</span>
            </div>

            <ul class="text-sm m-0 pl-4 mb-3">
                <li class="mb-1">
                    Ein <strong>Bankarbeitstag</strong> ist ein Tag, der weder Samstag noch Sonntag
                    noch ein hinterlegter Feiertag ist.
                </li>
                <li class="mb-1">
                    Die Feiertage sind gepflegte Konfiguration: sie stehen je Jahr im
                    <strong>Bankkalender</strong> des BM-Moduls, nicht im Programmcode.
                </li>
                <li class="mb-1">
                    Die <strong>Tagesart</strong> (Kalendertage (KT) oder Bankarbeitstage (BT)) ist je
                    Mahnstufe für jeden der vier Werte — Postlauf, Zahlungsfrist, interner Puffer,
                    Karenz Mahnstart — einzeln konfigurierbar; sie steht bei jedem Wert der
                    Mahnstufen.
                </li>
                <li>
                    Ob ein berechneter Stichtag auf den nächsten Bankarbeitstag
                    <strong>verschoben</strong> wird, wenn er auf einen Samstag, Sonntag oder
                    Feiertag fällt, ist ebenfalls je Stichtag (kommunizierter Stichtag, interner
                    Stichtag, Mahnstart Pfad A) konfigurierbar — z. B. weil Mahnstufe 1 nur eine
                    Erinnerung ist und keine qualifizierte Mahnung.
                </li>
            </ul>

            @if (derivationLoading) {
                <p-skeleton height="3rem"></p-skeleton>
            } @else if (derivationError) {
                <p-message severity="error"
                           text="Der Zustand des Bankfeiertagskalenders konnte nicht geladen werden."
                           styleClass="w-full">
                </p-message>
            } @else if (calendarInfo) {
                <!-- Wording is mandated (requirement 7.4 / binding constraint 2):
                     year and count only, NO country, NO tenant claim. -->
                <div class="flex align-items-center gap-2 mb-2">
                    <i class="fa-regular fa-circle-info text-gray-600"></i>
                    <span class="text-sm font-medium">
                        Hinterlegte Feiertage für {{ calendarInfo.year }}: {{ calendarInfo.holidayCount }}
                    </span>
                </div>

                @if (!calendarInfo.calendarConfigured) {
                    <p-message severity="warn"
                               text="Für dieses Jahr ist kein Bankfeiertagskalender gepflegt. In der Beispielrechnung wirken damit faktisch nur Wochenenden — echte Feiertage werden nicht berücksichtigt."
                               styleClass="w-full mb-2">
                    </p-message>
                }

                @if (calendarInfo.holidaysInWindow?.length) {
                    <div class="flex flex-column gap-1">
                        <span class="text-xs font-bold text-gray-600 uppercase">
                            Feiertage, die die Beispielrechnung getroffen haben
                        </span>
                        <div class="flex flex-wrap gap-2">
                            @for (holiday of calendarInfo.holidaysInWindow; track holiday.date) {
                                <p-tag [value]="(holiday.date | date:'dd.MM.yyyy') + ' · ' + holiday.name"
                                       severity="secondary">
                                </p-tag>
                            }
                        </div>
                    </div>
                }
            }
        </div>

        <!-- ============================================================ -->
        <!-- 5. Level transitions (requirement 7.5)                       -->
        <!-- ============================================================ -->
        <div class="surface-card border-round-lg border-1 border-300 p-3">
            <div class="flex align-items-center gap-2 mb-3">
                <i class="fa-regular fa-arrow-right-arrow-left text-primary"></i>
                <span class="text-lg font-semibold">Stufenübergänge</span>
            </div>

            @if (structureLoading) {
                <p-skeleton height="4rem"></p-skeleton>
            } @else if (structureError) {
                <p-message severity="error"
                           text="Die Stufenübergänge konnten nicht geladen werden. Bitte laden Sie die Seite erneut."
                           styleClass="w-full">
                </p-message>
            } @else if (!transitions.length) {
                <p-message severity="info"
                           text="Es ist kein Stufenübergang konfiguriert. Ohne Übergang bleibt eine Mahnung auf ihrer Startstufe stehen — legen Sie die Übergänge unter „Stufenübergänge“ an."
                           styleClass="w-full">
                </p-message>
            } @else {
                <div class="flex flex-column gap-2">
                    @for (transition of transitions; track transition.id) {
                        <div class="border-round-md border-1 border-300 p-2">
                            <div class="flex align-items-center gap-2 flex-wrap mb-1">
                                <span class="font-medium">
                                    {{ transition.fromLevelDtoName || 'Mahnungsstart' }}
                                </span>
                                <i class="fa-regular fa-arrow-right text-gray-600"></i>
                                <span class="font-medium">{{ transition.toLevelDtoName || '—' }}</span>
                                @if (transitionTypeLabel(transition.transitionType); as transitionTypeText) {
                                    <p-tag [value]="transitionTypeText" severity="info"></p-tag>
                                }
                                @if (isInactive(transition.entityStatus)) {
                                    <p-tag [value]="entityStatusLabel(transition.entityStatus)"
                                           severity="warn">
                                    </p-tag>
                                }
                            </div>

                            <div class="flex flex-wrap gap-3 text-sm text-gray-600">
                                @if (transition.paymentMethod != null) {
                                    <span>
                                        Zahlart: <strong>{{ paymentMethodLabel(transition.paymentMethod) }}</strong>
                                    </span>
                                }
                                @if (balanceCheckLabel(transition.balanceCheck); as balanceCheckText) {
                                    <span>Saldoprüfung: <strong>{{ balanceCheckText }}</strong></span>
                                }
                                @if (transition.checkLogicApply && transition.checkLogicDtoName) {
                                    <span class="text-blue-700">
                                        Prüflogik: <strong>{{ transition.checkLogicDtoName }}</strong>
                                    </span>
                                }
                            </div>
                        </div>
                    }
                </div>
            }
        </div>

        <!-- ============================================================ -->
        <!-- 6. Charges (requirement 7.7)                                 -->
        <!-- ============================================================ -->
        <div class="surface-card border-round-lg border-1 border-300 p-3">
            <div class="flex align-items-center gap-2 mb-3">
                <i class="fa-regular fa-coins text-primary"></i>
                <span class="text-lg font-semibold">Gebühren</span>
            </div>

            @if (structureLoading) {
                <p-skeleton height="4rem"></p-skeleton>
            } @else if (structureError) {
                <p-message severity="error"
                           text="Die Gebühren konnten nicht geladen werden. Bitte laden Sie die Seite erneut."
                           styleClass="w-full">
                </p-message>
            } @else if (!charges.length) {
                <p-message severity="info"
                           text="Für diesen Mahnungstyp ist keine Gebühr konfiguriert — es werden keine Mahngebühren gebucht."
                           styleClass="w-full">
                </p-message>
            } @else {
                <div class="flex flex-column gap-2">
                    @for (charge of charges; track charge.id) {
                        <div class="flex align-items-center gap-2 flex-wrap text-sm border-round-md border-1 border-300 p-2">
                            <span class="font-medium">
                                {{ charge.customerBillingTransactionTypeDtoName || 'Ohne Buchungsart' }}
                            </span>
                            @if (charge.amountRangeApply) {
                                <p-tag severity="secondary"
                                       [value]="'Betragsbereich ' + (charge.amountRangeFrom | number:'1.2-2')
                                                + ' – ' + (charge.amountRangeTo | number:'1.2-2')
                                                + ' ' + (charge.currency || 'EUR')">
                                </p-tag>
                            }
                            @if (charge.removable) {
                                <p-tag value="Entfernbar" severity="secondary"></p-tag>
                            }
                            @if (charge.editable) {
                                <p-tag value="Bearbeitbar" severity="secondary"></p-tag>
                            }
                            @if (isInactive(charge.entityStatus)) {
                                <p-tag [value]="entityStatusLabel(charge.entityStatus)" severity="warn"></p-tag>
                            }
                        </div>
                    }
                </div>
            }
        </div>

        <!-- ============================================================ -->
        <!-- 7. Stop conditions (requirement 7.8)                         -->
        <!-- ============================================================ -->
        <div class="surface-card border-round-lg border-1 border-300 p-3">
            <div class="flex align-items-center gap-2 mb-3">
                <i class="fa-regular fa-circle-stop text-primary"></i>
                <span class="text-lg font-semibold">Stoppbedingungen</span>
            </div>

            @if (structureLoading) {
                <p-skeleton height="4rem"></p-skeleton>
            } @else if (structureError) {
                <p-message severity="error"
                           text="Die Stoppbedingungen konnten nicht geladen werden. Bitte laden Sie die Seite erneut."
                           styleClass="w-full">
                </p-message>
            } @else if (!stops.length) {
                <p-message severity="info"
                           text="Es ist keine Stoppbedingung konfiguriert — das Mahnverfahren endet damit nur über die Stufenlogik, nicht über eine Saldo- oder Logikprüfung."
                           styleClass="w-full">
                </p-message>
            } @else {
                <div class="flex flex-column gap-2">
                    @for (stop of stops; track stop.id) {
                        <div class="border-round-md border-1 border-300 p-2">
                            <div class="flex align-items-center gap-2 flex-wrap mb-1">
                                <span class="font-medium">{{ stop.name || 'Ohne Bezeichnung' }}</span>
                                @if (stop.atLevelDtoName) {
                                    <p-tag [value]="'Bei Stufe: ' + stop.atLevelDtoName" severity="secondary"></p-tag>
                                }
                                @if (stopConditionTypeLabel(stop.stopConditionType); as stopTypeText) {
                                    <p-tag [value]="stopTypeText" severity="info"></p-tag>
                                }
                                @if (isInactive(stop.entityStatus)) {
                                    <p-tag [value]="entityStatusLabel(stop.entityStatus)" severity="warn"></p-tag>
                                }
                            </div>

                            <div class="flex flex-wrap gap-3 text-sm text-gray-600">
                                @if (balanceFieldLabel(stop.balanceField); as balanceFieldText) {
                                    <span>Saldenfeld: <strong>{{ balanceFieldText }}</strong></span>
                                }
                                @if (stop.threshold != null) {
                                    <span>
                                        Schwellwert: <strong>{{ stop.threshold | number:'1.2-2' }} €</strong>
                                    </span>
                                }
                                <!-- Rohwert prüfen, nicht das Label: paymentMethodLabel liefert bei
                                     null einen Gedankenstrich und würde die Zeile immer rendern. -->
                                @if (stop.paymentMethod != null) {
                                    <span>
                                        Zahlart: <strong>{{ paymentMethodLabel(stop.paymentMethod) }}</strong>
                                    </span>
                                }
                                @if (stopWaitingPeriodLabel(stop); as waitingPeriodText) {
                                    <span>
                                        Wartezeit: <strong>{{ waitingPeriodText }}</strong>
                                    </span>
                                }
                                @if (stop.checkLogicApply && stop.checkLogicDtoName) {
                                    <span class="text-blue-700">
                                        Prüflogik: <strong>{{ stop.checkLogicDtoName }}</strong>
                                    </span>
                                }
                            </div>

                            <!-- Ohne Wartezeit endet die Mahnung sofort, sobald die Bedingung
                                 zutrifft. Das ist eine bewusste Einstellung und kein fehlender
                                 Wert — nur sieht man es der Regel sonst nirgends an. -->
                            @if (stopHasNoWaitingPeriod(stop)) {
                                <div class="text-xs text-gray-600 mt-1">
                                    Ohne Wartezeit: die Mahnung endet sofort, sobald die
                                    Stoppbedingung erfüllt ist.
                                </div>
                            }
                        </div>
                    }
                </div>
            }
        </div>

        <!-- ============================================================ -->
        <!-- 8. Special handlings: collective empty state (requirement 7.9) -->
        <!-- The rules themselves hang off the levels and are rendered      -->
        <!-- there; this block only covers the "no rule at all" case.       -->
        <!-- ============================================================ -->
        @if (!structureLoading && !structureError && levels.length && !specialHandlingCount) {
            <div class="surface-card border-round-lg border-1 border-300 p-3">
                <div class="flex align-items-center gap-2 mb-3">
                    <i class="fa-regular fa-hand text-primary"></i>
                    <span class="text-lg font-semibold">Sonderbehandlungen</span>
                </div>
                <p-message severity="info"
                           text="Für keine Mahnstufe ist eine Sonderbehandlung konfiguriert (Übergabe Sachbearbeiter, Abschreibung, Einfrieren, manuelle Bearbeitung). Die Stufen laufen damit ohne Sonderweg durch."
                           styleClass="w-full">
                </p-message>
            </div>
        }

        <!-- ============================================================ -->
        <!-- 9. Extension points (requirement 7.10, COMPACT)              -->
        <!-- ============================================================ -->
        <div class="surface-card border-round-lg border-1 border-300 p-3">
            <div class="flex align-items-center gap-2 mb-3">
                <i class="fa-regular fa-puzzle-piece text-primary"></i>
                <span class="text-lg font-semibold">Erweiterungspunkte</span>
            </div>

            <p class="text-sm text-gray-600 mt-0 mb-3">
                Erweiterungspunkte erklären, warum an bestimmten Stellen des Mahnverfahrens
                zusätzliche Dialoge oder Schritte auftauchen.
            </p>

            @if (structureLoading) {
                <p-skeleton height="4rem"></p-skeleton>
            } @else if (structureError) {
                <p-message severity="error"
                           text="Die Erweiterungspunkte konnten nicht geladen werden. Bitte laden Sie die Seite erneut."
                           styleClass="w-full">
                </p-message>
            } @else if (!exitCount) {
                <p-message severity="info"
                           text="Es ist kein Erweiterungspunkt konfiguriert — das Mahnverfahren läuft ohne zusätzliche Dialoge oder Zwischenschritte."
                           styleClass="w-full">
                </p-message>
            } @else {
                <div class="flex flex-column gap-3">
                    @for (group of exitGroups; track group.label) {
                        <div>
                            <span class="text-xs font-bold text-gray-600 uppercase">{{ group.label }}</span>
                            <div class="flex flex-column gap-1 mt-1">
                                @for (exit of group.items; track exit.id) {
                                    <div class="flex align-items-center gap-2 flex-wrap text-sm">
                                        @if (exitExecutionTypeLabel(exit.executionType); as executionTypeText) {
                                            <p-tag [value]="executionTypeText" severity="secondary"></p-tag>
                                        }
                                        <span class="font-medium">
                                            {{ exitArtefactLabel(exit) || exit.alias || 'Ohne Artefakt' }}
                                        </span>
                                        @if (exit.dunningTypeLevelDtoName) {
                                            <span class="text-gray-600">
                                                Stufe: {{ exit.dunningTypeLevelDtoName }}
                                            </span>
                                        }
                                        <p-tag [value]="exit.activeStatus ? 'Aktiv' : 'Inaktiv'"
                                               [severity]="exit.activeStatus ? 'success' : 'warn'">
                                        </p-tag>
                                        @if (exit.canAbort) {
                                            <p-tag value="Kann abbrechen" severity="secondary"></p-tag>
                                        }
                                    </div>
                                }
                            </div>
                        </div>
                    }
                </div>
            }
        </div>

        <!-- Requirement 7.11 / 11.6: state the limits of this view. -->
        <p-message severity="info"
                   text="Prüflogiken werden hier nur beim Namen genannt, nicht ausgewertet. Ob eine Stufe im Einzelfall greift, hängt zusätzlich von Saldenstand, Pausen und dem Ergebnis der Logik ab. Diese Ansicht zeigt Konfiguration, nicht den Verlauf einer konkreten Mahnung."
                   styleClass="w-full">
        </p-message>

    </div>
}
